import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { subscribeToEventBouts, subscribeToDiscipline, subscribeToEvent, startNextPoolBout, releasePoolSeat, confirmPoolResults } from "../firebase/services";
import { getPoolAccessPoolId } from "../firebase/accessSessions";
import CodeQR from "../components/CodeQR";
import logo from "../assets/logo.png";
import UnavailableView from "../components/UnavailableView";

export default function RefPool() {
  const { eventId, disciplineId, poolName } = useParams();
  const navigate = useNavigate();
  const [bouts, setBouts] = useState([]);
  const [disc, setDisc] = useState(null);
  const [event, setEvent] = useState(null);
  const [starting, setStarting] = useState(false);
  const [confirmingPool, setConfirmingPool] = useState(false);

  useEffect(() => {
    const unsub = subscribeToEventBouts(eventId, (allBouts) => {
      setBouts(allBouts.filter(b => b.disciplineId === disciplineId && b.poolName === decodeURIComponent(poolName))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    });
    return unsub;
  }, [eventId, disciplineId, poolName]);

  useEffect(() => {
    const unsub = subscribeToDiscipline(eventId, disciplineId, setDisc);
    return unsub;
  }, [eventId, disciplineId]);

  useEffect(() => {
    const unsub = subscribeToEvent(eventId, setEvent);
    return unsub;
  }, [eventId]);

  const decodedPoolName = decodeURIComponent(poolName);
  const pool = disc?.pools?.find(p => p.name === decodedPoolName);
  const accessPoolId = getPoolAccessPoolId(eventId, disciplineId, decodedPoolName);
  const completed = bouts.filter(b => b.status === "confirmed").length;
  const activeBout = bouts.find(b => b.id === pool?.activeBoutId);
  const boutsRemaining = bouts.some(b => b.status !== "confirmed");
  const canStartNext = boutsRemaining && (!activeBout || activeBout.status === "confirmed");
  const allBoutsConfirmed = bouts.length > 0 && completed === bouts.length;
  const poolConfirmed = pool?.status === "confirmed";

  const handleStartNext = async () => {
    setStarting(true);
    try {
      const nextBoutId = await startNextPoolBout(eventId, disciplineId, decodedPoolName);
      if (nextBoutId) navigate(`/ref/${eventId}/${nextBoutId}`);
    }
    catch (err) { console.error(err); alert(`Failed to start next bout: ${err.message || err}`); }
    setStarting(false);
  };

  const handleUnlockJudge = async (name) => {
    if (!window.confirm(`Unlock ${name}'s seat? They'll be able to select their name again from the pool code screen.`)) return;
    try { await releasePoolSeat(eventId, disciplineId, decodedPoolName, "judge", name); }
    catch (err) { console.error(err); alert("Failed to unlock."); }
  };

  const handleConfirmPool = async () => {
    if (!window.confirm(`Confirm the full results for ${decodedPoolName}?\n\nThis cannot be undone.`)) return;
    setConfirmingPool(true);
    try { await confirmPoolResults(eventId, disciplineId, decodedPoolName); }
    catch (err) { console.error(err); alert(`Failed to confirm pool results: ${err.message || err}`); }
    setConfirmingPool(false);
  };

  if (!disc) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;
  if (!pool || !accessPoolId || pool.id !== accessPoolId) {
    return <UnavailableView eventId={eventId} title="Pool Access Ended"
      message="This pool was reset. Enter the current pool code to rejoin, or return to the event page for the current schedule." />;
  }

  return (
    <div className="min-h-screen p-5">
      <Link to="/" className="text-gray-600 text-sm no-underline hover:text-gray-400">← Home</Link>

      <div className="text-center mt-4">
        <img src={logo} alt="" className="w-56 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2 mb-3">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-[10px] font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      <div className="mt-1 mb-5">
        {event?.name && <div className="text-gray-600 text-xs mb-1">{event.name}</div>}
        <h1 className="font-display text-xl font-bold">{disc?.name ? `${disc.name} - ${decodedPoolName}` : decodedPoolName}</h1>
      </div>

      <div className="mb-5">
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-1.5">PROGRESS</div>
        <div className="flex items-baseline gap-1.5 mb-1.5">
          <span className="font-display text-2xl font-extrabold text-gray-200">{completed}</span>
          <span className="text-gray-600 text-sm">/ {bouts.length} bouts complete</span>
        </div>
        <div className="w-full h-2 bg-dark-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${bouts.length ? (completed / bouts.length) * 100 : 0}%`, background: "#4CAF50" }} />
        </div>
      </div>

      {(pool?.code || pool?.spectateCode) && (
        <div className="mb-5">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-1.5">CODES</div>
          {pool?.code && (
            <CodeQR path={`/pool/${eventId}/${disciplineId}/${encodeURIComponent(decodedPoolName)}?code=${pool.code}`}
              code={pool.code} codeLabel="OFFICIAL CODE" />
          )}
          {pool?.spectateCode && (
            <div className="mt-3 text-center">
              <div className="font-display text-[10px] font-bold text-gray-500 tracking-widest">SPECTATOR CODE</div>
              <div className="font-display text-2xl font-extrabold text-gray-300 tracking-[0.2em]">{pool.spectateCode}</div>
            </div>
          )}
        </div>
      )}

      {pool?.judges?.length > 0 && (
        <div className="mb-5">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-1.5">JUDGES</div>
          <div className="flex flex-wrap gap-1.5">
            {pool.judges.map((name) => {
              const isClaimed = (pool.claimedJudges || []).includes(name);
              return (
                <div key={name} className="flex items-center gap-1 px-2.5 py-1 bg-dark-800 border border-dark-500 rounded-lg text-xs"
                  style={{ color: isClaimed ? "#81C784" : "#bbb" }}>
                  <span>{name}</span>
                  {isClaimed && (
                    <button onClick={() => handleUnlockJudge(name)} aria-label={`Unlock judge seat for ${name}`}
                      className="min-w-6 min-h-6 text-gray-600 hover:text-yellow-500 bg-transparent border-none cursor-pointer text-xs ml-1"
                      title="Unlock seat (let them rejoin from another device)">🔓</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pool?.fighters?.length > 0 && (
        <div className="mb-5">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-1.5">COMPETITORS</div>
          <div className="flex flex-wrap gap-1.5">
            {pool.fighters.map((name) => (
              <span key={name} className="px-2.5 py-1 bg-dark-800 border border-dark-500 rounded-lg text-xs text-gray-300">{name}</span>
            ))}
          </div>
        </div>
      )}

      {canStartNext && (
        <button onClick={handleStartNext} disabled={starting}
          className="w-full p-4 mb-5 text-white font-display text-base font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer"
              style={{ background: "linear-gradient(135deg, #2E7D32, #1B5E20)" }}>
          {starting ? "Starting..." : "▶ Start Next Bout"}
        </button>
      )}

      {allBoutsConfirmed && !poolConfirmed && (
        <div className="bg-dark-700 border border-yellow-700 rounded-xl p-4 mb-5 text-center">
          <div className="font-display text-xs font-bold text-yellow-500 tracking-widest mb-2">ALL BOUTS COMPLETE</div>
          <p className="text-gray-500 text-xs mb-3">Review the scores below, then confirm the full pool results.</p>
          <button onClick={handleConfirmPool} disabled={confirmingPool}
            className="w-full p-3.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
            {confirmingPool ? "Confirming..." : <>&#10003; Confirm Pool Results</>}
          </button>
        </div>
      )}

      {poolConfirmed && (
        <div className="bg-dark-700 border border-green-action rounded-xl p-4 mb-5 text-center">
          <div className="font-display text-xs font-bold text-green-action-text tracking-widest">&#10003; POOL RESULTS CONFIRMED</div>
        </div>
      )}

      {bouts.length === 0 ? (
        <p className="text-gray-600 text-sm text-center py-8">No bouts found for this pool.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {bouts.map((bout, idx) => {
            const isActive = bout.id === pool?.activeBoutId && bout.status !== "confirmed";
            const isDone = bout.status === "confirmed";
            const statusStyle = isDone
              ? { color: "#4CAF50", label: "✓ Done" }
              : isActive
                ? { color: "#64B5F6", label: "● Active" }
              : { color: "#bbb", label: "Not started" };
            const judgesDone = Object.values(bout.judgeSubmissions || {}).filter(s => s.complete).length;
            const judges = bout.judges || [];
            const boutNumber = Number.isInteger(bout.order) ? bout.order + 1 : idx + 1;

            return (
              <button key={bout.id} onClick={() => navigate(`/ref/${eventId}/${bout.id}`)}
                className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors"
                style={{ opacity: isDone || isActive ? 1 : 0.6 }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xs font-bold text-gray-600">#{boutNumber}</span>
                    <span className="font-display text-base font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
                    <span className="text-gray-600 text-xs">vs</span>
                    <span className="font-display text-base font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
                  </div>
                  <span className="font-display text-xs font-bold" style={{ color: statusStyle.color }}>{statusStyle.label}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  {judges.length > 0 ? <span>{judgesDone}/{judges.length} judged</span> : <span className="text-yellow-700">No judges</span>}
                  {bout.confirmedResult && (
                    <span className="ml-auto">
                      <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</span>
                      <span className="mx-1 text-gray-700">–</span>
                      <span className="font-display font-bold" style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</span>
                    </span>
                  )}
                </div>
                {judges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {judges.map(j => (
                      <span key={j} className="px-1.5 py-0.5 bg-dark-800 border border-dark-500 rounded text-[10px] text-gray-500">{j}</span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
